// NodeAnalytics.js

/**
 * NodeAnalytics.js
 * 
 * Centralized helpers for view + engagement logging.
 */

// Services
import { createEngagement } from 'services/engagements/EngagementWriteService';
import { recordNodeFieldChanges } from 'services/nodes/NodeFieldChangeLogService';
import { recordNodeView } from 'services/nodeviews/NodeViewService';

// Utilities
import { EngagementType } from 'utilities/Enums';
import { generateKey } from 'utilities/Keys';
import { getResolvedFields } from 'utilities/NodeType';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import { buildUserSummary } from 'utilities/Users';

// View logging deduplication helpers
const VIEW_DEDUPE_WINDOW_MS = 5 * 60 * 1000; // Only log a view once per 5 minutes per session
const VIEW_SESSION_STORAGE_KEY = 'nodal:viewedNodes';

// Logging sanitization limits
const MAX_LOG_STRING_LENGTH = 1000;
const MAX_LOG_ARRAY_ITEMS = 25;
const MAX_LOG_OBJECT_KEYS = 25;
const MAX_LOG_DEPTH = 3;

// In-memory cache of recent views for the session
const inMemoryViewCache = new Map();
let sessionCacheLoaded = false;

// Core activity field paths to always log
const CORE_ACTIVITY_FIELD_PATHS = [
	'title',
	'description',
	'tags',
	'coverUrl',
	'permissions',
];

// Default values for specific paths
const PATH_DEFAULTS = new Map([
	['tags', []],
]);

// Suffix indicating a form field type
const FORM_FIELD_SUFFIX = '_form';

/**
 * buildActorSummary
 * 
 * Constructs a standardized actor summary object from various actor representations.
 * 
 * @param {object} actor - The actor object (user, node, profile, etc.).
 * 
 * @returns {object|null} - The constructed actor summary, or null if invalid.
 */
const buildActorSummary = (actor) => {

	// Validate actor
	if (!actor) return null;

	// Determine profile node
	const profileNode = actor.profileNode || actor.node || actor.profile || actor.data_profile || null;
	
	// Determine display user
	const displayUser = buildUserSummary(actor) || null;

	// Build summary fields
	const key = profileNode?.key || actor.key || actor.uid || null;
	const userKey = actor.key || actor.uid || profileNode?.userKey || key || null;
	const type = profileNode?.type || 'USER';
	const title = profileNode?.title || profileNode?.name || actor.name || actor.displayName || displayUser?.name || '';

	// Return summary object
	return {
		key,
		userKey,
		user: displayUser,
		type,
		title,
		coverUrl: profileNode?.coverUrl || actor.coverUrl || null,
		url: profileNode?.url || actor.url || null,
		url_thumb: profileNode?.url_thumb || actor.url_thumb || null,
		storagePath: profileNode?.storagePath || actor.storagePath || null,
	};
};

/**
 * buildLoggingPathsForField
 * 
 * Given a schema field definition, builds the set of data paths to log for that field.
 * 
 * @param {object} field - The schema field definition.
 * @param {string} ownerTypeKey - The type key of the owning node.
 * 
 * @returns {Set<string>} - A set of data paths to log for the field.
 */
const buildLoggingPathsForField = (field, ownerTypeKey) => {

	// Set of paths to return
	const paths = new Set();

	// Validate field
	if (!field || !field.type) {
		return paths;
	}

	// Determine source key
	const sourceKey = String(field.source || ownerTypeKey || '').trim().toLowerCase();

	// Determine if source is specified
	const hasSource = sourceKey.length > 0;

	// Build data key prefix if source is specified
	const dataKey = hasSource ? `data_${sourceKey}` : null;

	// Check if field is a form field
	const isFormField = typeof field.type === 'string' && field.type.endsWith(FORM_FIELD_SUFFIX);

	// Handle form fields
	if (isFormField) {
		if (dataKey) {
			paths.add(dataKey);
		}
		return paths;
	}

	// Standard field handling
	const fieldName = typeof field.name === 'string' ? field.name.trim() : '';
	if (!fieldName) {
		return paths;
	}

	// Primary data path
	if (dataKey) {
		paths.add(`${dataKey}.${fieldName}`);
	}

	// If no dataKey, log at top level
	if (!dataKey) {
		paths.add(fieldName);
	}

	return paths;
};

/**
 * hasRecentView
 * 
 * Checks if a view for the given node key has been logged recently in this session.
 * 
 * @param {string} nodeKey - The key of the node to check.
 * @param {number} nowMs - The current timestamp in milliseconds.
 * 
 * @returns {boolean} - True if a recent view exists, false otherwise.
 */
const hasRecentView = (nodeKey, nowMs) => {

	// Load session cache if not already loaded
	loadSessionViewCache();

	// Prune stale views
	const lastLogged = inMemoryViewCache.get(nodeKey);

	// Check if last logged is within dedupe window
	if (!Number.isFinite(lastLogged)) return false;

	// Return whether within dedupe window
	return nowMs - lastLogged < VIEW_DEDUPE_WINDOW_MS;
};

/**
 * loadSessionViewCache
 * 
 * Loads the view cache from sessionStorage into in-memory cache.
 */
const loadSessionViewCache = () => {
	
	// Avoid loading multiple times
	if (sessionCacheLoaded) return;
	sessionCacheLoaded = true;

	// Ensure window is available
	if (typeof window === 'undefined') return;

	try {

		// Load from sessionStorage
		const stored = window.sessionStorage?.getItem(VIEW_SESSION_STORAGE_KEY);
		if (!stored) return;

		// Parse and populate in-memory cache
		const parsed = JSON.parse(stored);
		if (parsed && typeof parsed === 'object') {
			Object.entries(parsed).forEach(([key, timestamp]) => {
				const numericTimestamp = Number(timestamp);
				if (key && Number.isFinite(numericTimestamp)) {
					inMemoryViewCache.set(key, numericTimestamp);
				}
			});
		}
	} catch (err) {
		console.warn('⚠️ Unable to restore view cache from sessionStorage:', err);
	}
};

/**
 * persistSessionViewCache
 * 
 * Persists the in-memory view cache to sessionStorage.
 */
const persistSessionViewCache = () => {

	// Ensure window is available
	if (typeof window === 'undefined') return;

	// Persist to sessionStorage
	try {
		const payload = Object.fromEntries(inMemoryViewCache.entries());
		window.sessionStorage?.setItem(VIEW_SESSION_STORAGE_KEY, JSON.stringify(payload));
	} catch (err) {
		console.warn('⚠️ Unable to persist view cache to sessionStorage:', err);
	}
};

/**
 * pruneStaleViews
 * 
 * Removes stale entries from the in-memory view cache.
 * 
 * @param {number} nowMs - The current timestamp in milliseconds.
 */
const pruneStaleViews = (nowMs) => {
	const cutoff = nowMs - VIEW_DEDUPE_WINDOW_MS;
	for (const [key, timestamp] of inMemoryViewCache.entries()) {
		if (!Number.isFinite(timestamp) || timestamp < cutoff) {
			inMemoryViewCache.delete(key);
		}
	}
};

/**
 * rememberView
 * 
 * Records a view for the given node key in the in-memory and session cache.
 * 
 * @param {string} nodeKey - The key of the node being viewed.
 * @param {number} nowMs - The current timestamp in milliseconds.
 */
const rememberView = (nodeKey, nowMs) => {

	// Load session cache if not already loaded
	loadSessionViewCache();

	// Prune stale views
	pruneStaleViews(nowMs);

	// Record the view
	inMemoryViewCache.set(nodeKey, nowMs);

	// Persist to sessionStorage
	persistSessionViewCache();
};

/**
 * sanitizeForActivity
 * 
 * Sanitizes a value for safe inclusion in activity logs.
 * 
 * @param {any} value - The value to sanitize.
 * @param {number} depth - The current recursion depth.
 * 
 * @returns {any} - The sanitized value.
 */
const sanitizeForActivity = (value, depth = 0) => {

	// Handle null/undefined
	if (value == null) return null;

	// Handle special date-like objects
	if (typeof value?.toDate === 'function' || typeof value?.toMillis === 'function') {
		try {
			if (typeof value.toDate === 'function') {
				return value.toDate().toISOString();
			}
			if (typeof value.toMillis === 'function') {
				return value.toMillis();
			}
		} catch (err) {
			// fall through
		}
	}

	// Handle Date objects
	if (value instanceof Date) {
		return value.toISOString();
	}

	// Handle strings
	if (typeof value === 'string') {
		if (value.length <= MAX_LOG_STRING_LENGTH) return value;
		return `${value.slice(0, MAX_LOG_STRING_LENGTH)}…`;
	}

	// Handle numbers and booleans
	if (typeof value === 'number' || typeof value === 'boolean') {
		return value;
	}

	// Handle arrays
	if (Array.isArray(value)) {
		if (depth >= MAX_LOG_DEPTH) return '[Array]';
		const slice = value.slice(0, MAX_LOG_ARRAY_ITEMS).map((entry) => sanitizeForActivity(entry, depth + 1));
		if (value.length > MAX_LOG_ARRAY_ITEMS) {
			slice.push('…');
		}
		return slice;
	}

	// Handle objects
	if (typeof value === 'object') {
		if (depth >= MAX_LOG_DEPTH) return '[Object]';
		const result = {};
		const keys = Object.keys(value).sort();
		const limitedKeys = keys.slice(0, MAX_LOG_OBJECT_KEYS);
		limitedKeys.forEach((key) => {
			const sanitized = sanitizeForActivity(value[key], depth + 1);
			if (sanitized !== undefined) {
				result[key] = sanitized;
			}
		});
		if (keys.length > limitedKeys.length) {
			result.__truncated = true;
		}
		return result;
	}

	return String(value);
};

/**
 * normalizeForComparison
 * 
 * Normalizes a value for comparison by converting dates to timestamps and sorting object keys.
 * 
 * @param {any} value - The value to normalize.
 * 
 * @returns {any} - The normalized value.
 */
const normalizeForComparison = (value) => {

	// Handle null/undefined
	if (value == null) return null;

	// Handle special date-like objects
	if (typeof value?.toMillis === 'function') {
		try {
			return value.toMillis();
		} catch (err) {
			return value;
		}
	}

	// Handle Date objects
	if (value instanceof Date) {
		return value.getTime();
	}

	// Handle arrays
	if (Array.isArray(value)) {
		return value.map((entry) => normalizeForComparison(entry));
	}

	// Handle objects
	if (typeof value === 'object') {
		const result = {};
		Object.keys(value)
			.sort()
			.forEach((key) => {
				const entry = value[key];
				if (typeof entry === 'function') return;
				result[key] = normalizeForComparison(entry);
			});
		return result;
	}

	// Return primitive values as-is
	return value;
};

/**
 * areValuesEqual
 * 
 * Compares two values for equality after normalization.
 * 
 * @param {any} a - The first value.
 * @param {any} b - The second value.
 * 
 * @returns {boolean} - True if values are equal, false otherwise.
 */
const areValuesEqual = (a, b) => {
	const normalizedA = normalizeForComparison(a);
	const normalizedB = normalizeForComparison(b);
	return JSON.stringify(normalizedA) === JSON.stringify(normalizedB);
};

/**
 * getValueAtPath
 * 
 * Retrieves a nested value from an object based on a dot-separated path.
 * 
 * @param {object} source - The source object.
 * @param {string} path - The dot-separated path string.
 * 
 * @returns {any} - The value at the specified path, or undefined if not found.
 */
const getValueAtPath = (source, path) => {
	if (!source || !path) return undefined;
	const segments = String(path).split('.');
	let current = source;
	for (const segment of segments) {
		if (current == null) return undefined;
		current = current[segment];
	}
	return current;
};

/**
 * getLoggedSchemaFieldPaths
 * 
 * Retrieves the data paths of schema fields marked for logging for a given node.
 * 
 * @param {object} node - The node object.
 * 
 * @returns {array} - An array of data paths to log.
 */
const getLoggedSchemaFieldPaths = (node) => {

	// Validate node and type
	const typeKey = node?.type;
	if (!typeKey) return [];

	// Get resolved schema fields
	const resolvedFields = getResolvedFields(typeKey, { includeHidden: true, excludeSecured: true });

	// Collect paths
	const paths = new Set();

	// Iterate over fields to find logged ones
	resolvedFields.forEach((field) => {
		const loggedFlag = field?.logged === true;
		const legacyLoggedFlag = field?.logging === true
			|| field?.logging?.enabled === true
			|| field?.logging?.logged === true;
		if (!loggedFlag && !legacyLoggedFlag) return;

		const fieldPaths = buildLoggingPathsForField(field, typeKey);
		fieldPaths.forEach((path) => {
			if (!path) return;
			paths.add(path);
		});
	});

	return Array.from(paths);
};

/**
 * gatherActivityFieldPaths
 * 
 * Gathers all unique field paths to log for activity changes between two nodes.
 * 
 * @param {object} previousNode - The previous version of the node.
 * @param {object} nextNode - The next version of the node.
 * 
 * @returns {array} - An array of unique field paths to log.
 */
const gatherActivityFieldPaths = (previousNode, nextNode) => {

	// Set of unique paths
	const paths = new Set(CORE_ACTIVITY_FIELD_PATHS);

	// Add schema-logged field paths from both nodes
	getLoggedSchemaFieldPaths(previousNode || {}).forEach((path) => {
		if (path) paths.add(path);
	});

	// Add from next node as well
	getLoggedSchemaFieldPaths(nextNode || {}).forEach((path) => {
		if (path) paths.add(path);
	});

	// Return as array
	return Array.from(paths);
};

/**
 * applyPathDefault
 * 
 * Applies default values for specific paths if the provided value is null/undefined.
 * 
 * @param {string} path - The data path.
 * @param {any} value - The value to check and potentially replace with a default.
 * 
 * @returns {any} - The original value or the default if applicable.
 */
const applyPathDefault = (path, value) => {

	// Check for default
	if (value == null && PATH_DEFAULTS.has(path)) {
		const defaultValue = PATH_DEFAULTS.get(path);
		if (Array.isArray(defaultValue)) return [...defaultValue];
		if (typeof defaultValue === 'object' && defaultValue !== null) return { ...defaultValue };
		return defaultValue;
	}

	// Return original value
	return value;
};

const buildFieldLabel = (path, node = null) => {
	const normalizedPath = String(path || '').trim();
	if (!normalizedPath) return 'Field';

	const typeKey = typeof node?.type === 'string' ? node.type.trim() : '';
	const dataKey = typeKey ? `data_${typeKey.toLowerCase()}` : '';
	if (dataKey && normalizedPath === dataKey) {
		const fields = getResolvedFields(typeKey, { includeHidden: true, excludeSecured: true });
		const formField = fields.find((field) => (
			typeof field?.type === 'string' && field.type.endsWith('_form') && field.label
		));
		if (formField?.label) return formField.label;

		const typeDef = getNodeType(typeKey);
		const typeLabel = String(typeDef?.label || '').trim();
		if (typeLabel) return typeLabel;
		return 'details';
	}

	const segments = normalizedPath.split('.');
	const fallback = segments[segments.length - 1] || normalizedPath;
	if (/^[a-zA-Z0-9]{16,}$/.test(fallback) || /^data_[a-zA-Z0-9]{12,}$/i.test(normalizedPath)) {
		return 'details';
	}

	const normalized = fallback.replace(/[_-]/g, ' ').trim();
	if (!normalized) return 'Field';

	return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

/**
 * logNodeFieldChanges
 * 
 * Logs field changes between two versions of a node to Firestore.
 * 
 * @param {object} previousNode - The previous version of the node.
 * @param {object} nextNode - The next version of the node.
 * @param {object} actor - The actor performing the change.
 * 
 * @returns {Promise<void>} - A promise that resolves when logging is complete.
 */
export const logNodeFieldChanges = async (previousNode, nextNode, actor) => {

	// Validate inputs
	if (!previousNode || !nextNode) return;

	// Determine node key
	const nodeKey = nextNode.key || previousNode.key;
	if (!nodeKey) return;

	// Gather fields to check
	const fieldsToCheck = gatherActivityFieldPaths(previousNode, nextNode);
	if (!fieldsToCheck || fieldsToCheck.length === 0) return;

	// Detect changes
	const changes = [];

	// Check each field
	fieldsToCheck.forEach((path) => {

		// Get previous and next values with defaults applied
		const prevRaw = applyPathDefault(path, getValueAtPath(previousNode, path));
		const nextRaw = applyPathDefault(path, getValueAtPath(nextNode, path));

		// Compare values
		if (areValuesEqual(prevRaw, nextRaw)) {
			return;
		}

		// Record change
		changes.push({
			fieldPath: path,
			previousValue: prevRaw,
			newValue: nextRaw,
		});
	});

	// If no changes, exit
	if (changes.length === 0) {
		return;
	}

	// Attempt to log changes
	try {

		// Build actor summary and keys
		const actorSummary = buildActorSummary(actor);
		const actorKey = actorSummary?.userKey || actorSummary?.key || actor?.key || actor?.uid || null;
		const userKey = actor?.key || actor?.uid || actorSummary?.userKey || actorKey || null;

		// Record field changes
		await recordNodeFieldChanges({
			nodeKey,
			nodeType: nextNode.type || previousNode.type || null,
			actorKey,
			userKey,
			actor: actorSummary,
			changes: changes.map((change) => ({
				fieldPath: change.fieldPath,
				fieldLabel: buildFieldLabel(change.fieldPath, nextNode),
				previousValue: sanitizeForActivity(change.previousValue),
				newValue: sanitizeForActivity(change.newValue),
			})),
		});

	} catch (err) {
		console.warn('⚠️ Failed to log node field changes:', err);
	}
};

/**
 * Logs a node view to Firestore (_nodeviews collection).
 * 
 * @param {string} nodeKey - The key of the node being viewed
 * @param {object} user - The current user (with summary fields), or null for anonymous
 */
export const logNodeView = async (nodeKey, user, options = {}) => {

	// Validate node key
	if (!nodeKey) return;

	// Extract options
	const { force = false } = options;

	// Current timestamp
	const nowMs = Date.now();

	// Check for recent view unless forced
	if (!force && hasRecentView(nodeKey, nowMs)) {
		return;
	}

	// Attempt to log the view
	try {
		const viewUser = user?.key ? buildUserSummary(user) : null;
		await recordNodeView({
			nodeKey,
			userKey: user?.key || null,
			user: viewUser,
		});
		rememberView(nodeKey, nowMs);
	} catch (err) {
		console.warn('⚠️ Error logging node view:', err);
	}
};

/**
 * Logs a contribution engagement for a node.
 * 
 * @param {object} params - The parameters for logging the engagement.
 * @param {object} params.node - The node that received the contribution.
 * @param {object} params.user - The user who made the contribution.
 * @param {number} params.now - The timestamp of the contribution.
 * @param {object|null} params.itemSummary - Optional summary of the item; defaults to the node.
 * 
 * @returns {Promise<void>} - A promise that resolves when logging is complete.
 */
export const logContributionEngagement = async ({ node, user, now, itemSummary = null } = {}) => {

	// Validate inputs
	if (!node?.key || !user?.key || !now) return;

	// Build engagement object
	const engagementKey = generateKey();
	const engagement = {
		key: engagementKey,
		userKey: user.key,
		user: buildUserSummary(user),
		itemKey: node.key,
		itemSummary: itemSummary || node,
		targetKey: node.userKey,
		type: EngagementType.CONTRIBUTION_MADE,
		dateCreated: now,
	};

	// Create the engagement record
	await createEngagement({ key: engagementKey, engagement });
};

/**
 * removeChildFromActivityEvents
 * 
 * Removes references to a specific child node from an array of activity events.
 * If an event ends up with no child nodes after removal, it is omitted.
 * 
 * @param {array} events - The array of activity events.
 * @param {string} childKey - The key of the child node to remove.
 * 
 * @returns {array} - The updated array of activity events.
 */
export const removeChildFromActivityEvents = (events, childKey) => {

	// Handle null or undefined inputs
	if (!childKey) return Array.isArray(events) ? events : [];

	// Process each event to remove the specified child
	return (Array.isArray(events) ? events : [])
		.map((event) => {
			if (!event || !Array.isArray(event.childNodes)) return event;
			const remainingChildren = event.childNodes.filter((child) => child.key !== childKey);
			if (remainingChildren.length === event.childNodes.length) {
				return event;
			}
			if (remainingChildren.length === 0) {
				return null;
			}
			return {
				...event,
				childNodes: remainingChildren,
			};
		})
		.filter(Boolean);
};

/**
 * cloneActivityEvents
 * 
 * Deep clones an array of activity events.
 * 
 * @param {array} events - The activity events to clone.
 * 
 * @returns {array} - The cloned activity events.
 */
export const cloneActivityEvents = (events) => (
	Array.isArray(events)
		? events.map((event) => ({ ...event }))
		: []
);
